import type { ComponentType } from 'react';
import React, { useMemo } from 'react';
import type { JsonLd } from 'json-ld-types';

import type { CardState } from '@atlaskit/linking-common/store';
import { getStatus } from '@atlaskit/linking-common/utils/get-status';

import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useStoreOptions as useStoreOptionsOld } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { getUrlAndMetadataStatus } from '@confluence/smart-card-provider/entry-points/cacheKey.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';

const useInitialState = () => {
	return useMemo(() => {
		const ssrProvidersData: { editorCardProvider?: Record<string, JsonLd.Response> } =
			window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] || {};
		const ssrData = ssrProvidersData.editorCardProvider;
		if (!ssrData) {
			return undefined;
		}

		return Object.entries(ssrData).reduce<Record<string, CardState>>((acc, [cacheKey, details]) => {
			const { metadataStatus: cacheMetadataStatus, url } = getUrlAndMetadataStatus(cacheKey);
			const status = getStatus(details);
			const metadataStatus = status === 'resolved' ? cacheMetadataStatus : 'resolved';
			if (acc[url]?.metadataStatus === 'resolved' && metadataStatus !== 'resolved') {
				return acc;
			}

			acc[url] = {
				status,
				details,
				metadataStatus,
			};

			return acc;
		}, {});
	}, []);
};

const useStoreOptions = () => {
	const isPageSSR = isPageSSRd();
	const initialState = useInitialState();
	const oldInitialState = window['__SSR_SMARTLINKS__'];
	const oldStoreOptions = useStoreOptionsOld(oldInitialState, isPageSSR);

	return useMemo(() => {
		if (initialState) {
			return {
				initialState,
			};
		}

		return oldStoreOptions.storeOptions;
	}, [oldStoreOptions, initialState]);
};

export function withSmartCardProviderContext<T>(WrappedComponent: ComponentType<T>) {
	return (props: any): React.JSX.Element => {
		const { isAdminHubAIEnabled } = useSessionData();

		const storeOptions = useStoreOptions();

		const content = <WrappedComponent {...props} />;

		return (
			<>
				<ConfluenceSmartCardProvider
					{...getSmartCardRenderers()}
					storeOptions={storeOptions}
					isAdminHubAIEnabled={isAdminHubAIEnabled}
				>
					{content}
				</ConfluenceSmartCardProvider>
			</>
		);
	};
}
